
import {
  getTomorrowReportFile,
  getDivisions,
  getTomorrowReportSplitData,
  getTomorrowReportShedData,
  getTomorrowReportSchedPDF,
  getTomorrowReportTobeSchedData,
  getTomorrowReportTobeSchedPDF,
} from '@/api';
import DateSelect from '@/components/dashBoard/selectsFields/components/DateSelect/DateSelect.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import { format } from 'date-fns';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';
import { blobDownloader } from '@/utils/Common';
import { Division, Header } from '@/models/main';
import { TomorrowReportData, TomorrowTobeSchedReportData } from '@/models/reports';
import * as Sentry from '@sentry/vue';
import { utcToZonedTime } from 'date-fns-tz';

@Component({
  components: {
    BackButton,
    DateSelect,
    Button,
  },
})
export default class TomorrowReport extends Vue {
  private isLoading = false;
  private isDownloading = false;
  private dates: string[] = [];
  private selectedDivision = '';
  private divisions: Division[] = [];
  private headers: Header[] = [
    { text: 'Id', value: 'rockhopper_id', align: 'left' },
    { text: 'Customer', value: 'customer', align: 'left' },
    { text: 'Reference', value: 'reference', align: 'left' },
    { text: 'Suburb', value: 'suburb', align: 'left' },
    { text: 'Driver', value: 'driver', align: 'left' },
    { text: 'Qty', value: 'qty', align: 'left' },
    { text: 'Products', value: 'products', align: 'left', sortable: false },
    // { text: 'Link', value: 'link', align: 'left' },
  ];
  private headersTobeShed: Header[] = [
    { text: 'Id', value: 'rockhopper_id', align: 'left' },
    { text: 'Name', value: 'name', align: 'left' },
    { text: 'Suburb', value: 'suburb', align: 'left' },
    { text: 'Departed at', value: 'departed_at', align: 'left' },
    { text: 'Split to Base', value: 'split_to_base', align: 'left' },
    { text: 'Scheduled', value: 'scheduled', align: 'left' },
  ];
  private reportSplitData: TomorrowReportData[] = [];
  private reportShedData: TomorrowReportData[] = [];
  private reportTobeShedData: TomorrowTobeSchedReportData[] = [];

  // Date lookup
  private changeDate(dates: string[]): void {
    this.dates = dates;
  }

  private getDivisionList() {
    getDivisions().then(res => {
      this.divisions = res;
    });
  }

  private async getReportData() {
    this.reportShedData = [];
    this.reportSplitData = [];
    this.reportTobeShedData = [];
    this.isLoading = true;
    await getTomorrowReportShedData(this.dates, this.selectedDivision)
      .then(res => {
        this.reportShedData = res;
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Tomorrow report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      });
    await getTomorrowReportSplitData(this.dates, this.selectedDivision)
      .then(res => {
        this.reportSplitData = res;
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Tomorrow report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      });
    await getTomorrowReportTobeSchedData(this.dates, this.selectedDivision)
      .then(res => {
        this.reportTobeShedData = res;
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Tomorrow report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      });
    this.isLoading = false;
  }

  /**
   * Download a general report file by division and date
   * @private
   */
  private downloadTomorrowGeneralReport() {
    // download loader should be used, but I don't want to spent time on template to implement it
    // so using isLoading for now
    this.isDownloading = true;
    getTomorrowReportFile(this.dates, this.selectedDivision)
      .then(res => {
        blobDownloader(res);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Tomorrow report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isDownloading = false;
      });
  }

  /**
   * Download "to be shed" report file by division and date
   * @private
   */
  private downloadTomorrowTobeShedReport() {
    // download loader should be used, but I don't want to spent time on template to implement it
    // so using isLoading for now
    this.isDownloading = true;
    getTomorrowReportTobeSchedPDF(this.dates, this.selectedDivision)
      .then(res => {
        blobDownloader(res);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Tomorrow report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isDownloading = false;
      });
  }

  /**
   * Download a schedule report file by division and date
   * @private
   */
  private downloadTomorrowScheduleReport() {
    // download loader should be used, but I don't want to spent time on template to implement it
    // so using isLoading for now
    this.isDownloading = true;
    getTomorrowReportSchedPDF(this.dates, this.selectedDivision)
      .then(res => {
        blobDownloader(res);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Tomorrow report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isDownloading = false;
      });
  }

  private mounted(): void {
    this.getDivisionList();
    this.dates = [format(utcToZonedTime(new Date(), timeZoneDefault), 'yyyy-MM-dd')];
  }
}
